import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { STORAGE_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged in
  const [registrationOpen, setRegistrationOpen] = useState(false);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
      setUser(false);
      try {
        const { data } = await api.get("/auth/registration-open");
        setRegistrationOpen(!!data?.open);
      } catch (_) {
        setRegistrationOpen(false);
      }
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(STORAGE_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem(STORAGE_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore
    }
    localStorage.removeItem(STORAGE_KEY);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, registrationOpen, login, register, logout, refreshMe, formatApiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
